import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Table } from 'app/custom-components';
import { Box, Typography } from '@material-ui/core';
import { getUserRegion, milkCategoryTypes, pluralize, toTitleCase } from '../../../../utils';
import { StateMap } from '../../../constants/region-utils';
import { getRouteRegionLabel } from '../../form-components/ReactHookProducerFormSelects';

function UserPreviewDataTable({ name, props: { label, helperText } }) {
    const region = getUserRegion();
    const { control, watch } = useFormContext();
    const type = watch('type');
    const isDynamic = watch('is_dynamic');

    const getColumns = (previewUsers) => {
        const baseColumns = [
            {
                field: 'name',
                headerName: 'Name',
                flex: 1,
                valueGetter: (params) => {
                    const user = params.row;
                    return user.name || '';
                },
            },
            {
                field: type === 'transport' ? 'hauler_number' : 'license_number',
                headerName: `${type === 'transport' ? 'Hauler' : 'License'} Number`,
                flex: 1,
                valueGetter: (params) => {
                    const user = params.row;
                    return user.license_number || user.hauler_number || '';
                },
            },
            {
                field: 'milk_category',
                headerName: 'Milk Category',
                flex: 1,
                valueGetter: (params) => milkCategoryTypes[params.row.milk_category] || '',
            },
            {
                field: 'state',
                headerName: 'State',
                valueGetter: (params) => {
                    return StateMap[params.row.state] || params.row.state || '';
                },
            },
            {
                field: 'federal_order',
                headerName: 'Federal Order',
            },
            {
                field: 'status',
                headerName: 'Status',
            },
            {
                field: 'pay_group',
                headerName: 'Pay Group',
            },
            {
                field: 'self_haul',
                headerName: 'Self Haul',
                valueGetter: (params) => {
                    if (params.row.self_haul == null) return '';
                    return params.row.self_haul ? 'Yes' : 'No';
                },
            },
            {
                field: 'route_region',
                headerName: getRouteRegionLabel(region),
            },
            {
                field: 'frozen',
                headerName: 'Colostrum Type',
                valueGetter: (params) => {
                    if (params.row.frozen == null) return '';
                    return params.row.frozen ? 'Frozen' : 'Fresh';
                },
            },
        ];

        const usersWithCustomFields = previewUsers.filter((user) => Array.isArray(user.custom_fields) && user.custom_fields.length > 0);
        const allCustomFields = usersWithCustomFields.flatMap((user) => user.custom_fields);

        let customFieldColumns = [];

        if (allCustomFields.length > 0) {
            customFieldColumns = allCustomFields
                .map((field) => ({
                    field: field._id,
                    headerName: toTitleCase(field.attribute, '_'),
                    valueGetter: (params) => {
                        const user = params.row;
                        if (!Array.isArray(user.custom_fields)) return '';

                        const customField = user.custom_fields.find((cf) => cf._id === field._id);
                        return customField ? customField.value : '';
                    },
                    valueFormatter: ({ value }) => {
                        if (typeof value === 'boolean') {
                            return value ? 'Yes' : 'No';
                        }

                        return value;
                    },
                }))
                .filter((col, index, self) => index === self.findIndex((c) => c.field === col.field)); // dedup
        }

        return [...baseColumns, ...customFieldColumns];
    };

    return (
        <Controller
            name={name}
            control={control}
            render={({ field: { value } }) => {
                const previewUsers = (isDynamic && value) || [];

                if (!previewUsers || previewUsers.length === 0) {
                    return (
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                {label}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                No users found in preview
                            </Typography>
                        </Box>
                    );
                }

                return (
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {label} ({previewUsers.length.toLocaleString()} {pluralize('user', previewUsers.length)})
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            {helperText}
                        </Typography>
                        <Box
                            sx={{
                                marginTop: 2,
                            }}
                        >
                            <Table
                                columns={getColumns(previewUsers)}
                                rows={previewUsers}
                                getRowId={(row) => row._id || row.id || row.license_number || row.hauler_number || `${row.name}-${row.state}-${row.federal_order}`}
                                loading={false}
                                autoHeight
                                hideFooterSelectedRowCount
                                disableColumnMenu={false}
                                disableColumnFilter={false}
                                disableColumnSort={false}
                                disableColumnResize={false}
                                pageSize={10}
                                rowsPerPageOptions={[10, 20, 50]}
                                pagination
                                PaperSx={{
                                    marginBottom: 0,
                                    boxShadow: 'none',
                                    border: 'none',
                                }}
                            />
                        </Box>
                    </Box>
                );
            }}
        />
    );
}

export default UserPreviewDataTable;
